import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Typography, ButtonPro } from 'components';
import { DialogBox } from 'components/organisms/DialogBox';
import { agent } from 'services/service';
import {
  LeaveAllocationEmployeeDTO,
  BulkLeaveAllocationResponseDTO
} from 'services/LeaveAllocation';
import { toast } from 'react-toastify';
import './index.scss';

type LeaveField = 'annualLeave' | 'casualLeave' | 'lieuLeave' | 'medicalLeave';

const LEAVE_FIELDS: { key: LeaveField; label: string }[] = [
  { key: 'annualLeave',  label: 'Annual' },
  { key: 'casualLeave',  label: 'Casual' },
  { key: 'lieuLeave',    label: 'Lieu' },
  { key: 'medicalLeave', label: 'Medical' },
];

const CURRENT_YEAR = new Date().getFullYear();

export const AnnualLeaveAllocationForm = () => {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [rows, setRows] = useState<LeaveAllocationEmployeeDTO[]>([]);
  const [savedRows, setSavedRows] = useState<LeaveAllocationEmployeeDTO[]>([]);
  const [dirtyUpns, setDirtyUpns] = useState<Set<string>>(new Set());
  const [selectedUpns, setSelectedUpns] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [yearError, setYearError] = useState('');

  // Bulk fill input values (empty string = not set)
  const [bulkAnnual,  setBulkAnnual]  = useState('');
  const [bulkCasual,  setBulkCasual]  = useState('');
  const [bulkLieu,    setBulkLieu]    = useState('');
  const [bulkMedical, setBulkMedical] = useState('');

  const prevYear = useRef(year);

  const loadAllocations = useCallback(async (y: number) => {
    setIsLoading(true);
    setSearch('');
    setSelectedUpns(new Set());
    setDirtyUpns(new Set());
    try {
      const res = await agent.LeaveAllocationService.getEmployeeAllocations(y);
      setRows(res.data);
      setSavedRows(res.data);
    } catch {
      toast.error('Failed to load leave allocations');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllocations(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const val = parseInt(raw);
    if (raw === '' || isNaN(val)) {
      setYearError('Please enter a valid year');
      return;
    }
    if (val < CURRENT_YEAR) {
      setYear(val);
      setYearError(`Year must be ${CURRENT_YEAR} or later`);
      return;
    }
    if (val > 2100) {
      setYear(val);
      setYearError('Year must be 2100 or earlier');
      return;
    }
    setYear(val);
    setYearError('');
  };

  const handleYearBlur = () => {
    if (yearError) return;
    if (year !== prevYear.current) {
      prevYear.current = year;
      loadAllocations(year);
    }
  };

  const handleYearKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  // Derived: rows visible after search filter
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.employeeName.toLowerCase().includes(q) ||
      r.epfNumber.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const dirtyCount = dirtyUpns.size;
  const allVisibleSelected =
    filteredRows.length > 0 &&
    filteredRows.every(r => selectedUpns.has(r.upn));

  // --- Cell editing ---
  const handleCellChange = (upn: string, field: LeaveField, raw: string) => {
    const val = raw === '' ? 0 : parseFloat(raw);
    if (isNaN(val) || val < 0) return;

    setRows(prev => prev.map(r => r.upn === upn ? { ...r, [field]: val } : r));

    // Mark dirty only if value actually differs from saved
    setDirtyUpns(prev => {
      const saved = savedRows.find(r => r.upn === upn);
      const next = new Set(prev);
      // Re-check all fields for this row after update
      const currentRow = rows.find(r => r.upn === upn);
      if (!saved || !currentRow) return next;
      const updated = { ...currentRow, [field]: val };
      const isDirty = LEAVE_FIELDS.some(f => updated[f.key] !== saved[f.key]);
      if (isDirty) next.add(upn); else next.delete(upn);
      return next;
    });
  };

  // --- Row selection ---
  const handleSelectAll = (checked: boolean) => {
    setSelectedUpns(prev => {
      const next = new Set(prev);
      filteredRows.forEach(r => checked ? next.add(r.upn) : next.delete(r.upn));
      return next;
    });
  };

  const handleSelectRow = (upn: string, checked: boolean) => {
    setSelectedUpns(prev => {
      const next = new Set(prev);
      checked ? next.add(upn) : next.delete(upn);
      return next;
    });
  };

  // --- Bulk fill ---
  const applyBulkToRows = (targets: LeaveAllocationEmployeeDTO[]) => {
    const parseBulk = (val: string): number | null => {
      if (val === '') return null;
      const n = parseFloat(val);
      return isNaN(n) || n < 0 ? null : n;
    };

    const updates: Record<LeaveField, number | null> = {
      annualLeave:  parseBulk(bulkAnnual),
      casualLeave:  parseBulk(bulkCasual),
      lieuLeave:    parseBulk(bulkLieu),
      medicalLeave: parseBulk(bulkMedical),
    };

    if (!updates.annualLeave && !updates.casualLeave && !updates.lieuLeave && !updates.medicalLeave) {
      toast.warning('Enter at least one valid value (must be 0 or greater) to apply');
      return;
    }

    const targetUpns = new Set(targets.map(r => r.upn));

    setRows(prev => prev.map(r => {
      if (!targetUpns.has(r.upn)) return r;
      const next = { ...r };
      if (updates.annualLeave  !== null) next.annualLeave  = updates.annualLeave;
      if (updates.casualLeave  !== null) next.casualLeave  = updates.casualLeave;
      if (updates.lieuLeave    !== null) next.lieuLeave    = updates.lieuLeave;
      if (updates.medicalLeave !== null) next.medicalLeave = updates.medicalLeave;
      return next;
    }));

    setDirtyUpns(prev => {
      const next = new Set(prev);
      targets.forEach(r => {
        const saved = savedRows.find(s => s.upn === r.upn);
        if (!saved) { next.add(r.upn); return; }
        const updated = {
          annualLeave:  updates.annualLeave  !== null ? updates.annualLeave  : r.annualLeave,
          casualLeave:  updates.casualLeave  !== null ? updates.casualLeave  : r.casualLeave,
          lieuLeave:    updates.lieuLeave    !== null ? updates.lieuLeave    : r.lieuLeave,
          medicalLeave: updates.medicalLeave !== null ? updates.medicalLeave : r.medicalLeave,
        };
        const isDirty = LEAVE_FIELDS.some(f => updated[f.key] !== saved[f.key]);
        if (isDirty) next.add(r.upn); else next.delete(r.upn);
      });
      return next;
    });
  };

  const handleApplyToAll = () => applyBulkToRows(filteredRows);

  const handleApplyToSelected = () => {
    const targets = filteredRows.filter(r => selectedUpns.has(r.upn));
    if (targets.length === 0) {
      toast.warning('Select at least one employee first');
      return;
    }
    applyBulkToRows(targets);
  };

  // --- Reset ---
  const handleReset = () => {
    setRows(savedRows);
    setDirtyUpns(new Set());
  };

  // --- Save ---
  const handleSaveClick = () => {
    if (dirtyCount === 0) return;
    setConfirmOpen(true);
  };

  // Renders a toast body that combines the summary message with the specific
  // per-employee reasons returned by the backend, so HR sees exactly which
  // employee was skipped and why instead of a generic failure message.
  const renderAllocationToast = (message: string, errors?: string[]) => (
    <div>
      <div>{message}</div>
      {errors && errors.length > 0 && (
        <ul className="allocation-toast-errors">
          {errors.map((err, idx) => (
            <li key={idx}>{err}</li>
          ))}
        </ul>
      )}
    </div>
  );

  const handleSaveConfirm = async () => {
    setConfirmOpen(false);
    setIsSaving(true);
    try {
      const dirtyEmployees = rows.filter(r => dirtyUpns.has(r.upn));
      const res = await agent.LeaveAllocationService.bulkUpsertAllocations({
        year,
        employees: dirtyEmployees
      });

      const { status, message, errors } = res.data;

      if (status === 'Error') {
        toast.error(renderAllocationToast(message, errors));
        return;
      }

      if (errors && errors.length > 0) {
        // Some employees saved, some were skipped — this is still a 200
        // response, but it's not a full success, so don't use the success toast.
        toast.warning(renderAllocationToast(message, errors));
      } else {
        toast.success(message);
      }

      // Refresh from server to reflect saved state
      await loadAllocations(year);
    } catch (error) {
      // The backend returns a 400 with the full ResponseDTO body when every
      // employee is blocked, which axios surfaces as a thrown error rather
      // than a resolved response — read the real message from there instead
      // of showing a generic failure toast.
      const responseData = axios.isAxiosError<BulkLeaveAllocationResponseDTO>(error)
        ? error.response?.data
        : undefined;

      if (responseData?.message) {
        toast.error(renderAllocationToast(responseData.message, responseData.errors));
      } else {
        toast.error('Failed to save leave allocations');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const saveLabel = isSaving
    ? 'Saving...'
    : dirtyCount > 0
      ? `Save ${dirtyCount} Modified Row${dirtyCount > 1 ? 's' : ''}`
      : 'No Changes';

  return (
    <div className="allocation-wrapper">
      {/* Header */}
      <div className="allocation-header">
        <div className="allocation-title">
          <Typography type="heading2" text="Annual Leave Allocation" />
        </div>
        <div className="year-selector">
          <label className="year-label">
            <Typography type="base_medium" text="Year" />
          </label>
          <input
            type="number"
            className={`year-input${yearError ? ' error' : ''}`}
            value={year}
            onChange={handleYearChange}
            onBlur={handleYearBlur}
            onKeyDown={handleYearKeyDown}
            min={CURRENT_YEAR}
            max={2100}
          />
          {yearError && <span className="year-error">{yearError}</span>}
        </div>
      </div>

      {/* Bulk Fill Panel */}
      <div className="bulk-fill-panel">
        <div className="bulk-fill-body">
        <div className="bulk-fill-header">
          <Typography type="base_medium" text="Bulk Fill" />
          <Typography type="small_regular" text="— Set values for multiple employees at once. Leave blank to skip that column." />
        </div>
        <div className="bulk-fill-inputs">
          {LEAVE_FIELDS.map(f => (
            <div key={f.key} className="bulk-field">
              <label className="bulk-field-label">{f.label}</label>
              <input
                type="number"
                className="bulk-input"
                placeholder="—"
                min={0}
                step={0.5}
                value={
                  f.key === 'annualLeave'  ? bulkAnnual  :
                  f.key === 'casualLeave'  ? bulkCasual  :
                  f.key === 'lieuLeave'    ? bulkLieu    : bulkMedical
                }
                onChange={e => {
                  const v = e.target.value;
                  if (f.key === 'annualLeave')  setBulkAnnual(v);
                  if (f.key === 'casualLeave')  setBulkCasual(v);
                  if (f.key === 'lieuLeave')    setBulkLieu(v);
                  if (f.key === 'medicalLeave') setBulkMedical(v);
                }}
              />
            </div>
          ))}
          <div className="bulk-actions">
            <ButtonPro
              label="Apply to All"
              onClick={handleApplyToAll}
              type="text"
              htmlType="button"
              disabled={isLoading}
            />
            <ButtonPro
              label={`Apply to Selected (${selectedUpns.size})`}
              onClick={handleApplyToSelected}
              type="text"
              htmlType="button"
              disabled={isLoading || selectedUpns.size === 0}
            />
          </div>
        </div>
        </div>
        <div className="bulk-fill-save-actions">
          <ButtonPro
            label="Reset Changes"
            onClick={handleReset}
            type="text"
            htmlType="button"
            disabled={dirtyCount === 0 || isSaving}
          />
          <ButtonPro
            label={saveLabel}
            onClick={handleSaveClick}
            type="primary"
            htmlType="button"
            disabled={dirtyCount === 0 || isSaving}
            isLoading={isSaving}
          />
        </div>
      </div>

      {/* Search + table */}
      <div className="allocation-table-section">
        <div className="table-toolbar">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name or EPF..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {dirtyCount > 0 && (
            <span className="dirty-badge">{dirtyCount} unsaved change{dirtyCount > 1 ? 's' : ''}</span>
          )}
        </div>

        {isLoading ? (
          <div className="loading-state">
            <Typography type="base_regular" text="Loading allocations..." />
          </div>
        ) : (
          <div className="table-scroll">
            <table className="allocation-table">
              <thead>
                <tr>
                  <th className="col-check">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={e => handleSelectAll(e.target.checked)}
                    />
                  </th>
                  <th className="col-epf">EPF</th>
                  <th className="col-name">Employee</th>
                  {LEAVE_FIELDS.map(f => (
                    <th key={f.key} className="col-leave">{f.label} Leave</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="empty-state">
                      {search ? 'No employees match your search.' : 'No employees found.'}
                    </td>
                  </tr>
                ) : (
                  filteredRows.map(row => {
                    const isDirty = dirtyUpns.has(row.upn);
                    const isSelected = selectedUpns.has(row.upn);
                    return (
                      <tr
                        key={row.upn}
                        className={[
                          isDirty    ? 'row-dirty'    : '',
                          isSelected ? 'row-selected' : '',
                        ].join(' ').trim()}
                      >
                        <td className="col-check">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={e => handleSelectRow(row.upn, e.target.checked)}
                          />
                        </td>
                        <td className="col-epf">{row.epfNumber}</td>
                        <td className="col-name">
                          {row.employeeName}
                          {isDirty && <span className="dirty-dot" title="Unsaved changes" />}
                        </td>
                        {LEAVE_FIELDS.map(f => (
                          <td key={f.key} className="col-leave">
                            <input
                              type="number"
                              className="cell-input"
                              value={row[f.key]}
                              min={0}
                              step={0.5}
                              onChange={e => handleCellChange(row.upn, f.key, e.target.value)}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="allocation-footer">
        <ButtonPro
          label="Reset Changes"
          onClick={handleReset}
          type="text"
          htmlType="button"
          disabled={dirtyCount === 0 || isSaving}
        />
        <ButtonPro
          label={saveLabel}
          onClick={handleSaveClick}
          type="primary"
          htmlType="button"
          disabled={dirtyCount === 0 || isSaving}
          isLoading={isSaving}
        />
      </div>

      {/* Confirmation dialog */}
      <DialogBox
        titleMessage={`Save allocations for ${dirtyCount} employee${dirtyCount > 1 ? 's' : ''} for ${year}? This will update their leave balances.`}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        setConfirm={setConfirmOpen}
        handleConfirm={handleSaveConfirm}
      />
    </div>
  );
};
